import React, {ChangeEvent, useState} from "react";
import {pageAuthorizedByGroup} from "../../fragments/PageAuthorization";
import {Link, useSearchParams} from "react-router-dom";
import {
	Box,
	Breadcrumbs,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography
} from "@mui/material";
import {PentaSplit} from "../../../components/common/PentaSplit";
import {graphQLServices} from "../../../services/GraphQLServices";
import {DocumentNode, useQuery} from "@apollo/client";
import {TRAVEL_GET_CALCULATION_DATA} from "../../../backend/multi-trip-travel/Queries";
import {ErrorBoundary} from "react-error-boundary";
import {generalError} from "../../fragments/ErrorElement";
import {CalculationData} from "./CalculationData";
import {LazyAccordion} from "../../../components/common/containers/LazyAccordion";
import {ExpandMore} from "@mui/icons-material";
import {CodeBlock} from "../../../components/common/CodeBlock";
import {translateFunction} from "../../../localization/TranslationService";
import {CurrentStageDisplay, HeaderSlot} from "../../../components/onsite/Footer";
import {HEALTHCARE_GET_CALCULATION_DATA, HEALTHCARE_GET_EXTERNAL_LOGS} from "../../../backend/healthcare/Queries";
import {HOME_CONTENT_PACKAGE_GET_CALCULATION_DATA} from "../../../backend/home-content-package/Queries";
import {SAVINGS_GET_CALCULATION_DATA} from "../../../backend/savings/Queries";
import {json} from "@codemirror/lang-json";
import {ExternalLog} from "./ExternalLog";
import {TRIPLEPLAY_GET_CALCULATION_DATA} from "../../../backend/triplePlay/Queries";

const translate = translateFunction("SureBasePortalMonitoring");

export const comparators = [
	"MultiTripTravel",
	"HealthCare",
	"HomeContentPackage",
	"TriplePlayV2",
	"Savings"
];

export const ComparisonInsightsPage = pageAuthorizedByGroup(function () {
	const [params] = useSearchParams();
	
	const [comparator, setComparator] = useState(() => params.get("comparator") ?? "");
	const [comparison, setComparison] = useState(() => params.get("comparison") ?? "");
	
	const basePath = "/monitoring/comparisons";
	
	function updateUrl(data: { comparator?: string; comparison?: string } = {}) {
		const _comparator = data.comparator ?? comparator;
		const _comparison = data.comparison ?? comparison;
		
		const queryParams = [{key: "comparator", value: _comparator}, {key: "comparison", value: _comparison}]
			.filter(it => it.value)
			.map(it => `${it.key}=${it.value}`)
			.join("&");
		const queryString = queryParams ? "?" + queryParams : "";
		
		window.history.replaceState(null, "", `${basePath}${queryString}`);
	}
	
	return <>
		<HeaderSlot><CurrentStageDisplay/></HeaderSlot>
		<PentaSplit
			top={<Breadcrumbs style={{margin: "5px"}}>
				<Link style={{color: "inherit", textDecoration: "unset"}}
					to="/monitoring">{translate("Monitoring")}</Link>
				<Typography color="text.primary">{translate("Comparisons")}</Typography>
			</Breadcrumbs>}
			center={<Stack style={{height: "100%"}} spacing={2}>
				<Typography>{translate("Comparison Insights")}</Typography>
				<Stack direction={"row"} spacing={2}>
					<FormControl>
						<InputLabel id={"comparator"}>{translate("Comparator")}</InputLabel>
						<Select
							style={{width: "200px"}}
							labelId={"comparator"}
							label={translate("Comparator")}
							variant={"standard"}
							value={comparator.toLowerCase()}
							onChange={event => {
								const value = event.target.value;
								setComparator(value);
								updateUrl({comparator: value});
							}}
						>
							<MenuItem key={"empty"} value={""} hidden={true} style={{display: "none"}}></MenuItem>
							{comparators.map(it =>
								<MenuItem
									key={it.toLowerCase()}
									value={it.toLowerCase()}
								>{translate(it)}</MenuItem>
							)}
						</Select>
					</FormControl>
					<TextField
						style={{width: "400px"}}
						label={translate("Comparison")}
						value={comparison}
						variant={"standard"}
						error={!!comparison && !isValidComparison(comparison)}
						onChange={(event: ChangeEvent<HTMLInputElement>) => {
						// onChange={event => {
							const value = event.target.value.trim();
							setComparison(value);
							updateUrl({comparison: value});
						}}
					/>
				</Stack>
				<ErrorBoundary FallbackComponent={generalError}>
					{isValid(comparator, comparison)
						? <ComparatorInsights comparator={comparator} comparison={comparison}/>
						: <></>}
				</ErrorBoundary>
			</Stack>}
		/>
	</>;
}, ["Admin", "Monitoring"]);

function isValid(comparator: string, comparison: string): boolean {
	return comparators.some(it => it.toLowerCase() === comparator) && isValidComparison(comparison);
}

function isValidComparison(comparison: string): boolean {
	return /(^|\/)[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/.test(comparison);
}

function ComparatorInsights(props: { comparator: string; comparison: string }) {
	const calculationData = useQuery(getCalculationDataQuery(props.comparator), {
		client: getClient(props.comparator),
		fetchPolicy: "no-cache",
		variables: {
			comparisonId: props.comparison,
		}
	});
	
	const externalLogsQuery = useQuery(getExternalDataQuery(props.comparator), {
		client: getClient(props.comparator),
		fetchPolicy: "no-cache",
		variables: {
			comparisonId: props.comparison,
		},
		skip: props.comparator !== "healthcare"
	});
	
	if (calculationData.loading || externalLogsQuery.loading)
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	
	const error = calculationData.error || externalLogsQuery.error;
	if (error)
		throw error;
	
	const data: CalculationData | null | undefined = calculationData.data.calculationData;
	if (!data)
		return <div>comparison with id {props.comparison} not found</div>;
	
	const externalLogs: ExternalLog[] = externalLogsQuery.data?.externalLogs;
	
	return <div style={{height: "100%"}}>
		<LazyAccordion
			summaryProps={{expandIcon: <ExpandMore/>}}
			summary={<Typography>{translate("Input")}</Typography>}>
			<CodeBlock
				name={`${props.comparison}-input.json`}
				syntax={json()}
			>{stringify(data.calculationInput)}</CodeBlock>
		</LazyAccordion>
		<LazyAccordion
			summaryProps={{expandIcon: <ExpandMore/>}}
			summary={<Typography>{translate("Output")}</Typography>}>
			<CodeBlock
				name={`${props.comparison}-output.json`}
				syntax={json()}
			>{stringify(data.calculationOutput)}</CodeBlock>
		</LazyAccordion>
		<LazyAccordion
			summaryProps={{expandIcon: <ExpandMore/>}}
			summary={<Typography>{translate("Traces")}</Typography>}>
			<CodeBlock
				name={`${props.comparison}-traces.json`}
				syntax={json()}
			>{stringify(data.traces)}</CodeBlock>
		</LazyAccordion>
		
		{externalLogs && externalLogs.map(it => {
			return <LazyAccordion
				key={it.name}
				summaryProps={{expandIcon: <ExpandMore/>}}
				summary={<Typography>{it.name}</Typography>}>
				<CodeBlock
					name={`${props.comparison}-log-${it.name}.txt`}
					syntax={json()}
				>{it.content}</CodeBlock>
			</LazyAccordion>;
		})}
	</div>;
}

function stringify(value: any) {
	return JSON.stringify(value, valueProvider, 4);
}

function valueProvider(key: string, value: any | null): any | null | undefined {
	if (value === null)
		return undefined;
	if (key === "__typename")
		return undefined;
	return value;
}

function getCalculationDataQuery(comparator: string): DocumentNode {
	if (comparator.toLowerCase() === "multitriptravel") {
		return TRAVEL_GET_CALCULATION_DATA;
	}
	if (comparator.toLowerCase() === "homecontentpackage") {
		return HOME_CONTENT_PACKAGE_GET_CALCULATION_DATA;
	}
	if (comparator.toLowerCase() === "savings") {
		return SAVINGS_GET_CALCULATION_DATA;
	}
	if (comparator.toLowerCase() === "tripleplayv2") {
		return TRIPLEPLAY_GET_CALCULATION_DATA;
	}
	return HEALTHCARE_GET_CALCULATION_DATA;
}

function getExternalDataQuery(comparator: string): DocumentNode {
	return HEALTHCARE_GET_EXTERNAL_LOGS;
}

function getClient(comparator: string) {
	if (comparator.toLowerCase() === "multitriptravel") {
		return graphQLServices.travelClient;
	}
	if (comparator.toLowerCase() === "healthcare") {
		return graphQLServices.healthCare;
	}
	if (comparator.toLowerCase() === "homecontentpackage") {
		return graphQLServices.homeContentPackage;
	}
	if (comparator.toLowerCase() === "savings") {
		return graphQLServices.savings;
	}
	
	if (comparator.toLowerCase() === "tripleplayv2") {
		return graphQLServices.triplePlay;
	}
	
	throw new Error(`Unrecognized comparator '${comparator}'.`);
}
